import { Button } from '@/components/ui/shadcn/button'
import { Label } from '@/components/ui/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { useState, useContext, useEffect } from 'react'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import { fetcher } from '@/lib/api/fetch'

const PROVIDERS = [
  { value: 'claude', label: 'Claude (Anthropic)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini (Google)' },
]

export default function AIProvider() {
  const authContext = useContext(AuthContext)
  const [settings, setSettings] = useState({
    primary: 'claude',
    secondary: 'openai',
    tertiary: 'gemini',
  })
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Load current settings from organization
    if (authContext.user.organization?.aiProviderSettings) {
      const orgSettings = authContext.user.organization.aiProviderSettings
      if (orgSettings.primary) {
        setSettings({
          primary: orgSettings.primary,
          secondary: orgSettings.secondary,
          tertiary: orgSettings.tertiary,
        })
      }
    }
  }, [authContext.user.organization])

  const selectedValues = [
    settings.primary,
    settings.secondary,
    settings.tertiary,
  ]
  const hasDuplicates = new Set(selectedValues).size !== selectedValues.length

  const saveSettings = async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await fetcher(
        `/api/admin/organization/${authContext.user.organization.id}/settings/ai-provider`,
        {
          method: 'PUT',
          body: JSON.stringify(settings),
        },
        authContext,
      )

      const data = await res.json()
      if (res.status !== 200) {
        setError(data.error || 'Failed to save settings')
        setLoading(false)
        return
      }

      // Update auth context with new settings
      authContext.setuser(
        {
          ...authContext.user,
          organization: {
            ...authContext.user.organization,
            aiProviderSettings: settings,
          },
        },
        () => {},
      )

      setSaved(true)
      setTimeout(() => {
        setSaved(false)
      }, 5000)
    } catch {
      setError('Failed to save settings')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = field => value => {
    setSettings({ ...settings, [field]: value })
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="grid gap-4">
        <div>
          <h6 className="text-base font-semibold">AI Provider Priority</h6>
          <p className="text-sm text-muted-foreground">
            Choose which AI providers to use and their fallback order. If the
            primary provider is unavailable, requests automatically fall back to
            the secondary, then tertiary.
          </p>
        </div>

        <ErrorAlerts error={error} />

        {saved && (
          <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
            <CheckCircle2 className="size-4" />
            <AlertDescription>
              AI provider settings saved successfully
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="primary-provider">Primary Provider</Label>
            <Select
              value={settings.primary}
              onValueChange={handleChange('primary')}
              disabled={loading}
            >
              <SelectTrigger id="primary-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map(p => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="secondary-provider">Secondary Provider</Label>
            <Select
              value={settings.secondary}
              onValueChange={handleChange('secondary')}
              disabled={loading}
            >
              <SelectTrigger id="secondary-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map(p => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tertiary-provider">Tertiary Provider</Label>
            <Select
              value={settings.tertiary}
              onValueChange={handleChange('tertiary')}
              disabled={loading}
            >
              <SelectTrigger id="tertiary-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map(p => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {hasDuplicates && (
          <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Each provider must be selected only once. Please choose a
              different provider for each priority level.
            </AlertDescription>
          </Alert>
        )}

        <div>
          <Button onClick={saveSettings} disabled={loading || hasDuplicates}>
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  )
}
